import * as React from 'react';
import { createPortal } from 'react-dom';
import { SiteSearchConfig } from '../../empracoTypes';
import { NoResult, SearchResultsList } from './site-search-results';
import { fetchMultiple, GroupedResponse } from './site-search-request';
import {CurrentSiteSearchContext} from './site-search-common';
import {useClickOutsideRef} from '../../util/hooks/useClickOutsideRef';
import {initMobileUi} from './site-search-mobile-ui';

export interface SiteSearchProps {
  parentId: string;
  config: SiteSearchConfig;
}

export interface ISearchRequest extends SiteSearchConfig {
  searchTerm: string;
  pageNo: number;
  searchType: string;
}

export default function SiteSearch({ parentId, config }: SiteSearchProps) {
  const [response, setResponse] = React.useState<GroupedResponse[]>([]);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [timer, setTimer] = React.useState<NodeJS.Timeout>();
  const [searching, setSearching] = React.useState<boolean>(false);
  const resRef = React.createRef<HTMLDivElement>();
  const inputRef = React.createRef<HTMLInputElement>();
  const buttonsRef = React.createRef<HTMLDivElement>();

  const clearSearch = () => {
    setSearchTerm('');
    setResponse([]);
  };

  useClickOutsideRef([resRef, inputRef, buttonsRef], clearSearch);
  React.useEffect(() => initMobileUi(), []);

  const fetchData = async (value: string) => {
    setSearching(true);
    setResponse([]);

    const req: ISearchRequest = {
      ...config,
      searchTerm: value,
      pageNo: 1,
      searchType: ''
    };

    req.searchAliasItems = req.searchAliasItems.filter(
      item => item.alias && !item.isRemovedFromSearch
    );

    req.searchAliasItems.forEach(item => {
      item.culture = config.culture;
    });

    setResponse(await fetchMultiple(config, req));
    setSearching(false);
  };

  const handleChange = async (value: string) => {
    setSearchTerm(value);

    if (value) {
      if (timer) {
        clearTimeout(timer);
      }

      const timeOutIdentifier = setTimeout(() => fetchData(value), 500);

      setTimer(timeOutIdentifier);
    }
  };

  const getSearchResult = () => {
    if (!searchTerm) {
      return null;
    }

    let cls = 'empori-site-search-result-list';

    if (config.headerName) {
      cls += ` ${config.headerName}`;
    }

    const content = (
      <div className={cls} ref={resRef}>
        {response && response.length > 0 && response.some(res => res.results && res.results.length > 0)
          ? <SearchResultsList />
          : <NoResult searching={searching} />}
      </div>
    );

    return config.inGrid ? content : createPortal(content, document.body);
  };

  return <CurrentSiteSearchContext.Provider value={{ config, response }}>
    <input
      ref={inputRef}
      type='text'
      className='empori-site-search-input'
      title={config.primeflow?.translations?.SearchAriaLabel ?? config.placeHolderText}
      placeholder={config.primeflow?.translations?.SearchBarPhrase ?? config.placeHolderText}
      name={parentId}
      value={searchTerm}
      onChange={async e => await handleChange(e.currentTarget.value)}
    />

    <div className='empori-site-search-actions' ref={buttonsRef}>
      {searchTerm &&
        <button
          className='empori-site-search-button clear-search'
          title='Clear search'
          onClick={clearSearch}>
          <i className='fa-regular fa-times'></i>
        </button>
      }

      <button className='empori-site-search-button' title='Search' onClick={() => inputRef.current?.focus()}>
        <i className='fa-regular fa-search'></i>
      </button>
    </div>

    {getSearchResult()}
  </CurrentSiteSearchContext.Provider>;
}
